'use client'

import { type TextProps } from '@builder.io/react/dist/types/src/blocks/Text'
import { useContext } from 'react'

import { CountdownContext } from '@src/components/CountdownWrapper/CountdownWrapper'
import { useClickAction } from '@src/hooks/useClickAction/useClickAction'
import { type UseClickActionArgs } from '@src/hooks/useClickAction/useClickActionArgs'
import { cn } from '@src/utils/tailwind'

import NumberCountdown from './NumberCountdown'

export const textStyleMap = {
  'Display Heading 1': 'text-heading-display-1',
  'Display Heading 2': 'text-heading-display-2',
  'Display Heading 3': 'text-heading-display-3',
  'Heading 1': 'text-heading-1',
  'Heading 2': 'text-heading-2',
  'Heading 3': 'text-heading-3',
  'Heading 4': 'text-heading-4',
  'Heading 5': 'text-heading-5',
  'Body XL': 'text-body-xl',
  'Body XL - Medium weight': 'text-body-xl font-medium',
  'Body LG': 'text-body-lg',
  'Body LG - Medium weight': 'text-body-lg font-medium',
  'Body MD': 'text-body-md', // TODO: remove this once all uses are gone
  'Body MD - Medium weight': 'text-body-md font-medium', // TODO: remove this once all uses are gone
  'Body Base': 'text-body-base',
  'Body Base - Medium weight': 'text-body-base font-medium',
  'Body SM': 'text-body-sm',
  'Body SM - Medium weight': 'text-body-sm font-medium',
  'Body XS': 'text-body-xs',
  'Body XS - Medium weight': 'text-body-xs font-medium',
}

type CustomTextProps = TextProps &
  UseClickActionArgs & {
    style: string
    timePeriod?: 'seconds' | 'minutes' | 'hours' | 'days'
    getValueFromCountdown?: boolean
    animated?: boolean
    builderBlock?: any
    animatedBackground?: string
  }

export const Text = (props: CustomTextProps) => {
  const {
    text,
    style = 'Body MD',
    timePeriod,
    getValueFromCountdown,
    animated,
    builderBlock,
    animatedBackground,
  } = props
  const countdown = useContext(CountdownContext)
  const { ClickWrapper, handlers, isWrapping } = useClickAction(props)
  const textStyle = textStyleMap[style as keyof typeof textStyleMap]
  const actionName = props.actionName
  const hasAction = !isWrapping && actionName && actionName !== 'none'
  const divButtonProps = hasAction
    ? {
        role: 'button',
        tabIndex: 0,
      }
    : {}

  const currentCountdown = countdown?.currentCountdown
  const selectedTimePeriod =
    timePeriod && !isNaN(currentCountdown?.[timePeriod]) ? currentCountdown?.[timePeriod] : null

  return (
    <ClickWrapper>
      {getValueFromCountdown && !isNaN(selectedTimePeriod) && selectedTimePeriod !== null ? (
        <NumberCountdown
          animated={!!animated}
          selectedTimePeriod={selectedTimePeriod}
          textStyle={textStyle}
          builderBlock={builderBlock}
          animatedBackground={animatedBackground as string}
          {...divButtonProps}
          {...(!isWrapping && handlers)}
        />
      ) : (
        <span
          {...divButtonProps}
          {...(!isWrapping && handlers)}
          suppressHydrationWarning
          className={cn('builder-text prose', textStyle, {
            'cursor-pointer': hasAction,
          })}
          dangerouslySetInnerHTML={{ __html: text }}
        ></span>
      )}
    </ClickWrapper>
  )
}
